import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import ReceiptEuro from 'assets/svg/icons/ReceiptEuro';
import AccountableLogoWordV2 from 'components/atoms/SVG/AccountableLogoWordV2';
import IntegrationBadge from './IntegrationBadge';
import NewBankIcon from 'assets/svg/icons/newBankIcon';
import FilePercentage from 'assets/svg/icons/FilePercentage';
import CoinsGroup from 'assets/svg/icons/CoinsGroup';

interface BadgeConfig {
  variant: 'revenues' | 'expenses' | 'taxes';
  labelKey: string;
  position: string;
  delay: number;
}

const BADGE_CONFIGS: BadgeConfig[] = [
  {
    variant: 'taxes',
    labelKey: 'taxes',
    position:
      'absolute top-8 left-0 max-[1360px]:top-24 max-[1360px]:left-12 w-fit',
    delay: 0.6,
  },
  {
    variant: 'expenses',
    labelKey: 'expenses',
    position:
      'absolute top-16 right-0 max-[1360px]:top-28 max-[1360px]:right-12 w-fit',
    delay: 0.8,
  },
  {
    variant: 'revenues',
    labelKey: 'revenues',
    position:
      'absolute bottom-4 max-[1360px]:bottom-16 left-1/2 -translate-x-1/2 w-fit',
    delay: 1.0,
  },
];

const ICON_MAP = {
  revenues: CoinsGroup,
  expenses: ReceiptEuro,
  taxes: FilePercentage,
};

const HowIsIntegratedVisual: FC = () => {
  const { t } = useTranslation();

  return (
    <div className="relative w-full max-w-[320px] mx-auto h-[300px]">
      {/* Outer dotted circle */}
      <div className="absolute inset-0 flex items-center justify-center">
        <motion.div
          className="w-80 h-80 rounded-full border border-dashed border-bank-300"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, ease: [0.34, 1.56, 0.64, 1] }}
        />
      </div>

      {/* Middle dotted circle */}
      <div className="absolute inset-0 flex items-center justify-center">
        <motion.div
          className="w-60 h-60 rounded-full border border-dashed border-bank-300"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{
            duration: 0.6,
            delay: 0.1,
            ease: [0.34, 1.56, 0.64, 1],
          }}
        />
      </div>

      {/* Inner dotted circle */}
      <div className="absolute inset-0 flex items-center justify-center">
        <motion.div
          className="w-44 h-44 rounded-full border border-dashed border-bank-300"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{
            duration: 0.6,
            delay: 0.2,
            ease: [0.34, 1.56, 0.64, 1],
          }}
        />
      </div>

      {/* Center badge - Accountable */}
      <div className="absolute inset-0 flex items-center justify-center">
        <motion.div
          className="flex items-center gap-2 px-3 py-2 bg-white rounded-full shadow-[0px_1px_2px_0px_rgba(27,27,27,0.1),0px_0px_0px_1px_#f6f6f6]"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{
            duration: 0.5,
            delay: 0.3,
            ease: [0.34, 1.56, 0.64, 1],
          }}
        >
          <NewBankIcon className="w-6 h-6 text-bank-600" />
          <AccountableLogoWordV2 size={80} className="text-bank-600" />
        </motion.div>
      </div>

      {/* Floating integration badges */}
      {BADGE_CONFIGS.map((config) => (
        <div key={config.variant} className={config.position}>
          <IntegrationBadge
            variant={config.variant}
            label={t(config.labelKey)}
            Icon={ICON_MAP[config.variant]}
            delay={config.delay}
          />
        </div>
      ))}
    </div>
  );
};

export default HowIsIntegratedVisual;
